import { first, isEmpty, isNil } from 'lodash';
import { CSSProperties, useCallback, useContext, useState } from 'react';
import { MBClearButton } from '@shared/components/MBButton';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { MBAutosizeTextInput } from '@shared/components/textInput';
import { AuditTextField } from '@shared/data/entity/fields/AuditTextField';
import { ColorThemeContext, Colors, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { FileUpload } from '@shared/types/fieldValues';
import { cn } from '@shared/util/classnames';
import { useFileUpload } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { AuditFieldFooterActions } from './AuditFieldFooterActions';
import { AuditFieldMedia } from './AuditFieldMedia';
import { EditFieldContainer } from './EditFieldContainer';
import styles from './AuditTextEditField.module.css';

interface Props {
  field: AuditTextField | null;
  component: API.AuditTextComponent;
  updateField?: (componentId: string, field: AuditTextField | null) => void;
  error?: string;
  showIcon?: boolean;
  style?: CSSProperties;
}

export function AuditTextEditField({
  field,
  component,
  updateField,
  error,
  showIcon,
  style,
}: Props) {
  const colors = useContext(ColorThemeContext);
  const [isFocused, setIsFocused] = useState(false);
  const isReadOnly = component.readonly || isNil(updateField);

  const text = field?.text ?? '';

  const onSave = useCallback(
    (value: string) => {
      updateField?.(
        component.id,
        !isEmpty(value)
          ? new AuditTextField({
              text: value,
              files: field?.files ?? [],
              notes: field?.notes ?? '',
            })
          : null,
      );
    },
    [component.id, updateField, field],
  );

  const onNotesChange = useCallback(
    (updatedNotes: string) => {
      updateField?.(
        component.id,
        new AuditTextField({ text, files: field?.files ?? [], notes: updatedNotes }),
      );
    },
    [component.id, updateField, text, field],
  );

  const onFileAdded = useCallback(
    (addedFiles: FileUpload[]) => {
      const file = first(addedFiles);
      if (!file) {
        return;
      }
      updateField?.(
        component.id,
        new AuditTextField({
          text,
          files: [...(field?.files ?? []), file],
          notes: field?.notes ?? '',
        }),
      );
    },
    [component.id, updateField, text, field],
  );

  const onFileRemoved = useCallback(
    (file: FileUpload) => {
      updateField?.(
        component.id,
        new AuditTextField({
          text,
          files: (field?.files ?? []).filter(f => f.url !== file.url),
          notes: field?.notes ?? '',
        }),
      );
    },
    [component.id, updateField, text, field],
  );

  const { showFilePicker, uploading } = useFileUpload({
    onFileAdded,
    onFileRemoved,
    multiple: false,
  });
  const hasFiles = !isEmpty(field?.files ?? []);
  const hasNotes = !isEmpty(field?.notes ?? '');
  const hasFilesOrNotes = hasFiles || hasNotes;

  return (
    <EditFieldContainer
      component={component}
      showIcon={showIcon ?? false}
      showLabel={false}
      showPlaceholder={false}
      error={error}
      style={style}
      notes={
        !!field?.notes && (
          <Text size="body2" color={colors.foregroundActive}>
            {field.notes}
          </Text>
        )
      }
      files={
        (field?.files ?? []).length > 0 && (
          <AuditFieldMedia
            files={field?.files ?? []}
            editable={!isReadOnly}
            onFileRemoved={onFileRemoved}
          />
        )
      }
      footer={
        !isReadOnly && (
          <AuditFieldFooterActions
            showFilePicker={showFilePicker}
            uploading={uploading}
            notes={field?.notes ?? ''}
            onNotesChange={onNotesChange}
          />
        )
      }
    >
      <View paddingHorizontal={2} paddingVertical={1}>
        <Text size="body1" color={colors.foregroundActive}>
          {component.label}
        </Text>
      </View>
      <View className={cn(styles.textContainer, !hasFilesOrNotes && styles.noFilesOrNotes)}>
        <div
          className={cn(
            styles.textInput,
            isFocused && styles.textInputFocused,
            !isNil(error) && styles.textInputError,
          )}
        >
          <MBAutosizeTextInput
            value={text}
            onChangeText={onSave}
            placeholder={i18n('card.blocks.question.answerPlaceholder')}
            disabled={isReadOnly}
            padding="none"
            style={!isEmpty(text) && !isReadOnly ? { paddingRight: grid(4.5) } : undefined}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />
          {!isEmpty(text) && !isReadOnly && (
            <div className={styles.clearButton}>
              <MBClearButton onPress={() => onSave('')} color={Colors.v2.gray30} />
            </div>
          )}
        </div>
      </View>
    </EditFieldContainer>
  );
}
